import React, { useState, useEffect } from "react";
import { Typography, Grid, Box, Button } from '@mui/material';
import { Container } from "@mui/system";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import axios from "axios";
import "./Team.css";
import TeamDraftYear from "../../components/TeamDraftYear/TeamDraftYear";
import TeamDraftProspectsSummary from "../../components/TeamDraftProspectsSummary/TeamDraftProspectsSummary";
import TeamDraftPickHistoryGrid from "../../components/TeamDraftPickHistoryGrid/TeamDraftPickHistoryGrid";

const INITIAL_TEAM_DRAFT_YEARS_VISIBLE = 4;

function Team() {
    const [error, setError] = useState(null);
    const [draftHistory, setDraftHistory] = useState([]);
    const [team, setTeam] = useState(null);
    const [draftPicks, setDraftPicks] = useState(null);
    const [draftGrades, setDraftGrades] = useState(null);
    const [draftProspectsSummary, setDraftProspectsSummary] = useState(null);
    const [draftSummaryLoading, setDraftSummaryLoading] = useState(true);
    const [pickHistory, setPickHistory] = useState(null);
    const [pickHistoryLoading, setPickHistoryLoading] = useState(true);
    const [showAllTeamDraftYears, setShowAllTeamDraftYears] = useState(false);

    const { team_id } = useParams();

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "/teams/" + team_id).then((response) => {
            setTeam(response.data);
        })
            .catch((error) => {
                setTeam(null);
                setError(error);
            });
    }, [team_id]);


    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "/draft_grades/team", { params: { "team_id": team_id } }).then((response) => {
            setDraftGrades(response.data);
        })
            .catch((error) => {
                setDraftGrades(null);
                setError(error);
            });
    }, [team_id]);

    useEffect(() => {
        axios.get(process.env.REACT_APP_API_URL + "/draft/history/" + team_id).then((response) => {
            setDraftHistory(response.data);
        })
            .catch((error) => {
                setDraftHistory([]);
                setError(error);
            });
    }, [team_id]);

    useEffect(() => {
        setShowAllTeamDraftYears(false);
    }, [team_id]);

    useEffect(() => {
        setDraftSummaryLoading(true);
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/teams/${team_id}/draft_prospects_summary`
            )
            .then((response) => {
                setDraftProspectsSummary(response.data);
                setDraftSummaryLoading(false);
            })
            .catch(() => {
                setDraftProspectsSummary(null);
                setDraftSummaryLoading(false);
            });
    }, [team_id]);

    useEffect(() => {
        setPickHistoryLoading(true);
        axios
            .get(
                `${process.env.REACT_APP_API_URL}/draft/teams/${team_id}/pick_history`
            )
            .then((response) => {
                setPickHistory(response.data);
                setPickHistoryLoading(false);
            })
            .catch(() => {
                setPickHistory(null);
                setPickHistoryLoading(false);
            });
    }, [team_id]);


    function formatTeamIdToName(team_id) {
        var name = team_id.split("_").map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(" ");
        return name;
    }

    function getGradeByYear(draft_year){
        if (draft_year != null && draftGrades != null){
            for (var i = 0; i < draftGrades.length; i++) {
                if (draftGrades[i].draft_year == draft_year) {
                    return draftGrades[i].grade;
                }
            }
        }
        return null;
    }

    const teamName = formatTeamIdToName(team_id);

    const draftYearBlocks =
        draftHistory && draftGrades ? draftHistory : [];
    const visibleDraftYearBlocks = showAllTeamDraftYears
        ? draftYearBlocks
        : draftYearBlocks.slice(0, INITIAL_TEAM_DRAFT_YEARS_VISIBLE);
    const hasMoreDraftYearBlocks =
        draftGrades &&
        draftHistory &&
        draftHistory.length > INITIAL_TEAM_DRAFT_YEARS_VISIBLE;

    return (
        <>
            <Helmet>
                <title>{teamName} - Hockey Pipeline</title>
                <meta name="description" content={`View ${teamName} NHL draft history, prospect picks, and draft grades on Hockey Pipeline.`} />
                <link rel="canonical" href={`https://hockeypipeline.com/team/${team_id}`} />
            </Helmet>
            <Container className="top-container">
                <Grid container rowSpacing={{ xs: 2, sm: 2, md: 3 }}>
                    {team && <Grid item xs={12} sm={12} md={12} >
                        <div className="container team-page-header">
                            <div className="team-page-header-title">
                                <img src={team.team_logo_url} className="team-logo" ></img>
                                <Typography variant="body1" className="team-name">
                                    {team.team_name}
                                </Typography>
                            </div>
                        </div>
                    </Grid>}

                    <TeamDraftPickHistoryGrid
                        key={team_id}
                        loading={pickHistoryLoading}
                        data={pickHistory}
                    />

                    <TeamDraftProspectsSummary
                        loading={draftSummaryLoading}
                        summary={draftProspectsSummary}
                    />

                    {draftHistory && draftGrades && (
                        <>
                            {visibleDraftYearBlocks.map((draft_year) => (
                                <TeamDraftYear
                                    key={draft_year.draft_year}
                                    draft_year={draft_year}
                                    draft_grade={getGradeByYear(draft_year.draft_year)}
                                />
                            ))}
                            {hasMoreDraftYearBlocks && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            py: 1,
                                            px: 1,
                                        }}
                                    >
                                        {!showAllTeamDraftYears && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "var(--text-muted)",
                                                    letterSpacing: "0.02em",
                                                }}
                                            >
                                                Showing {visibleDraftYearBlocks.length} of{" "}
                                                {draftYearBlocks.length} draft classes
                                            </Typography>
                                        )}
                                        <Button
                                            size="small"
                                            variant="text"
                                            onClick={() =>
                                                setShowAllTeamDraftYears((v) => !v)
                                            }
                                            sx={{
                                                color: "var(--brand-strong)",
                                                fontWeight: 600,
                                                textTransform: "none",
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            {showAllTeamDraftYears
                                                ? `Show fewer (${INITIAL_TEAM_DRAFT_YEARS_VISIBLE} years)`
                                                : `Show all ${draftYearBlocks.length} draft classes`}
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </Container >
        </>
    )
}

export default Team;